import { useCallback, useEffect, useRef, useState } from 'react';
import { fmtDateTime } from '../../../../utils/ruDates';

const rub = value => value == null ? '—' : `${Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;
const failure = err => typeof err?.detail === 'string' ? err.detail : err?.message || 'Не удалось выполнить запрос';
// Дата без времени из поля ввода — до конца этого дня по Москве.
const endOfDay = value => value ? new Date(`${value}T23:59:59+03:00`).toISOString() : null;
const LOCK_KIND = {
  manual: 'Зафиксирована вами',
  external_pending: 'Изменена вне сервиса — ждёт вашего решения',
  external_kept: 'Оставлена после правки вне сервиса',
};

function Sub({ children, warn = false }) {
  return <small className={warn ? 'oz-auto__unavailable' : 'oz-auto__muted'} style={{ display: 'block' }}>{children}</small>;
}

function PriceLog({ base, request, offer }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    request(`${base}/prices/log?offer_id=${encodeURIComponent(offer)}&limit=50`)
      .then(data => setItems(data.items))
      .catch(err => setError(failure(err)));
  }, [base, request, offer]);
  if (error) return <p role='alert' className='bidder__error'>{error}</p>;
  if (!items) return <p>Загрузка…</p>;
  if (!items.length) return <p className='oz-auto__muted'>Изменений цены пока нет.</p>;
  return <div className='oz-auto__table-scroll'><table>
    <thead><tr><th>Когда</th><th>Кто</th><th>Было → стало</th><th>Статус</th></tr></thead>
    <tbody>{items.map((row, index) => <tr key={`${row.at}-${index}`}>
      <td>{fmtDateTime(row.at)}</td>
      <td>{row.source_label}{row.details?.reason && <Sub>{row.details.reason}</Sub>}
        {row.details?.note && <Sub>{row.details.note}</Sub>}</td>
      <td>{row.old_price ?? '—'} → {row.new_price} ₽</td>
      <td>{row.status === 'ok' ? 'Записано' : `Ошибка${row.error ? `: ${row.error}` : ''}`}</td>
    </tr>)}</tbody>
  </table></div>;
}

function Until({ value, onChange, forever, onForever }) {
  return <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
    <label>До <input type='date' value={value} disabled={forever} onChange={e => onChange(e.target.value)} /></label>
    <label><input type='checkbox' checked={forever} onChange={e => onForever(e.target.checked)} /> бессрочно</label>
  </span>;
}

export default function OzonAutomationPrices({ base, request, busy: outerBusy }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // { offer, mode: 'lock' | 'keep' }
  const [until, setUntil] = useState('');
  const [forever, setForever] = useState(false);
  const [logOffer, setLogOffer] = useState(null);
  const [filter, setFilter] = useState('');
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    try {
      const next = await request(`${base}/prices`);
      if (id === requestId.current) { setData(next); setError(''); }
    } catch (err) { if (id === requestId.current) setError(failure(err)); }
  }, [base, request]);
  useEffect(() => { load(); }, [load]);

  const call = async (path, options) => {
    setBusy(true); setError('');
    try { await request(path, options); await load(); setEditing(null); }
    catch (err) { setError(failure(err)); }
    finally { setBusy(false); }
  };
  const expires = () => forever ? null : endOfDay(until);
  const ready = forever || !!until;
  const disabled = busy || outerBusy;

  if (!data) return <div className='oz-auto__panel'>{error
    ? <p role='alert' className='bidder__error'>{error}</p> : <p>Загрузка…</p>}</div>;

  const term = filter.trim().toLowerCase();
  const rows = data.rows.filter(row => !term
    || `${row.offer_id} ${row.name || ''} ${row.sku}`.toLowerCase().includes(term));

  const startEdit = (offer, mode) => { setEditing({ offer, mode }); setUntil(''); setForever(false); };
  const lockBody = offer => JSON.stringify({ offer_id: offer, expires_at: expires() });

  return <div className='oz-auto__stack'>
    {data.pending > 0 && <div className='oz-auto__notice'>
      <strong>Цену изменили вне сервиса: {data.pending} арт.</strong>
      <p>Автоматика на этих артикулах остановлена. Оставьте новую цену (зафиксируйте её) или верните контроль сервису.</p>
    </div>}
    {error && <p role='alert' className='bidder__error'>{error}</p>}

    <section className='oz-auto__panel'>
      <div className='oz-auto__panel-head'>
        <strong>Цены артикулов группы</strong>
        <div className='oz-auto__panel-actions'>
          <input type='search' placeholder='Артикул или название' value={filter}
            aria-label='Поиск по артикулам' onChange={e => setFilter(e.target.value)} />
          <button type='button' disabled={disabled} onClick={load}>Обновить</button>
        </div>
      </div>
      <p className='oz-auto__muted'>Цена — последняя, которую увидел сервис. Зафиксированный артикул автоматика не трогает:
        ни биддер, ни медиана, ни РРЦ, ни расписание. Цену, изменённую вне сервиса, сервис считает ориентиром
        и ждёт вашего решения.</p>
      <div className='oz-auto__table-scroll'><table>
        <thead><tr><th>Артикул</th><th>Цена</th><th>Что ведёт цену</th><th>Фиксация</th><th>Последнее изменение</th><th /></tr></thead>
        <tbody>{rows.map(row => {
          const lock = row.lock;
          const pending = lock?.kind === 'external_pending';
          const open = editing?.offer === row.offer_id;
          return [<tr key={row.offer_id} className={pending ? 'is-attention' : ''}>
            <td><strong>{row.name || row.offer_id}</strong><Sub>Артикул {row.offer_id} · SKU {row.sku}</Sub></td>
            <td>{rub(lock ? lock.price : row.price)}
              {row.price_seen_at && <Sub>на {fmtDateTime(row.price_seen_at)}</Sub>}</td>
            <td>{row.tools.length ? row.tools.map((tool, index) => <div key={`${tool.key}-${index}`}>
              {tool.label}{tool.action_price != null && <Sub>цена в акции {rub(tool.action_price)}</Sub>}
              {tool.floor != null && <Sub>не ниже {rub(tool.floor)}</Sub>}
            </div>) : <span className='oz-auto__muted'>никто</span>}</td>
            <td>{lock ? <>
              {LOCK_KIND[lock.kind] || lock.kind}
              <Sub>{lock.expires_at ? `до ${fmtDateTime(lock.expires_at)}` : 'бессрочно'}</Sub>
              {pending && lock.previous_price != null && <Sub warn>было {rub(lock.previous_price)}, стало {rub(lock.price)}</Sub>}
            </> : <span className='oz-auto__muted'>нет</span>}</td>
            <td>{row.last_change ? <>{fmtDateTime(row.last_change.at)}
              <Sub>{row.last_change.source_label}: {row.last_change.old_price ?? '—'} → {row.last_change.new_price} ₽</Sub></>
              : <span className='oz-auto__muted'>нет записей</span>}</td>
            <td>
              {pending ? <>
                <button type='button' className='oz-auto__primary' disabled={disabled}
                  onClick={() => startEdit(row.offer_id, 'keep')}>Оставить цену</button>
                <button type='button' disabled={disabled}
                  onClick={() => call(`${base}/prices/resolve`, { method: 'POST',
                    body: JSON.stringify({ offer_id: row.offer_id, keep: false }) })}>Вернуть контроль сервису</button>
              </> : lock ? <button type='button' disabled={disabled}
                onClick={() => call(`${base}/prices/lock?offer_id=${encodeURIComponent(row.offer_id)}`, { method: 'DELETE' })}>
                Снять фиксацию</button>
                : <button type='button' disabled={disabled || row.price == null}
                  title={row.price == null ? 'Цена ещё не считана — появится после следующего прохода.' : undefined}
                  onClick={() => startEdit(row.offer_id, 'lock')}>Зафиксировать</button>}
              <button type='button' disabled={disabled}
                onClick={() => setLogOffer(logOffer === row.offer_id ? null : row.offer_id)}>
                {logOffer === row.offer_id ? 'Скрыть журнал' : 'Журнал'}</button>
            </td>
          </tr>,
          open && <tr key={`${row.offer_id}-edit`}><td colSpan='6'>
            <strong>{editing.mode === 'keep' ? 'Оставить цену' : 'Зафиксировать текущую цену'} {rub(lock ? lock.price : row.price)}</strong>{' '}
            <Until value={until} onChange={setUntil} forever={forever} onForever={setForever} />{' '}
            <button type='button' className='oz-auto__primary' disabled={disabled || !ready}
              onClick={() => editing.mode === 'keep'
                ? call(`${base}/prices/resolve`, { method: 'POST',
                  body: JSON.stringify({ offer_id: row.offer_id, keep: true, expires_at: expires() }) })
                : call(`${base}/prices/lock`, { method: 'POST', body: lockBody(row.offer_id) })}>Сохранить</button>
            <button type='button' disabled={disabled} onClick={() => setEditing(null)}>Отмена</button>
          </td></tr>,
          logOffer === row.offer_id && <tr key={`${row.offer_id}-log`}><td colSpan='6'>
            <PriceLog base={base} request={request} offer={row.offer_id} /></td></tr>,
          ];
        })}</tbody>
      </table></div>
      {!rows.length && <p className='oz-auto__muted'>Артикулов не найдено.</p>}
    </section>
  </div>;
}
